import React from "react";

import { OptionNameValuePair } from "../../../lib/interfaces/types";
import SelectDropdownV2 from "../../../components/ui/selectdropdown/v2";
import {
  IDENTIFIER_RULES,
  IdentifierType,
  isIdentifierType,
  sanitizeIdentifierInput,
  validateIdentifier,
} from "../../../components/ui/formelement/v2/EditableField";

type ManagementFormFieldType = "text" | "number" | "select" | IdentifierType;

interface ManagementFormFieldProps {
  label: string;
  type?: ManagementFormFieldType;
  required?: boolean;
  value: any;
  onChange: (value: string) => void;
  disabled?: boolean;
  options?: OptionNameValuePair<string>[];
  showError?: boolean;
}

const ManagementFormField: React.FC<ManagementFormFieldProps> = ({
  label,
  type = "text",
  required = false,
  value,
  onChange,
  disabled = false,
  options = [],
  showError = false,
}) => {
  const selectedOption = options.find(
    (option) => String(option.value) === String(value),
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === "aadhaar" || type === "din") {
      if (
        event.key.length === 1 &&
        !/^[0-9]$/.test(event.key) &&
        !event.ctrlKey &&
        !event.metaKey
      ) {
        event.preventDefault();
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;

    onChange(
      isIdentifierType(type)
        ? sanitizeIdentifierInput(type, rawValue)
        : rawValue,
    );
  };

  const inputError = isIdentifierType(type)
    ? validateIdentifier(type, value)
    : null;

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <label className="text-[14px] font-medium leading-[14px] text-[#0A0A0A]">
        {label}
        {required && <span className="text-[#DC2626]">*</span>}
      </label>

      {type === "select" ? (
        <SelectDropdownV2
          name={label}
          options={options}
          selectedOption={selectedOption}
          onSelectionChange={(option) => onChange(String(option.value))}
          disabled={disabled}
          enableSearch={false}
          containerClassName="w-full"
          labelClassName="h-9 w-full rounded-[8px] border border-[#E5E5E5] bg-white px-3 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]"
          selectedContainerClassName="text-[#0A0A0A]"
        />
      ) : (
        <>
          <input
            type={type === "number" ? "number" : "text"}
            value={value ?? ""}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={`Enter ${label.toLowerCase()}`}
            disabled={disabled}
            maxLength={
              isIdentifierType(type) ? IDENTIFIER_RULES[type].length : undefined
            }
            className="h-9 w-full rounded-[8px] border border-[#E5E5E5] bg-white px-3 text-[14px] font-normal leading-5 text-[#0A0A0A] shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] outline-none placeholder:text-[#737373] disabled:cursor-not-allowed disabled:bg-[#F5F5F5]"
          />

          {showError && inputError && (
            <p className="text-xs font-normal leading-4 text-red-600">
              {inputError}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default ManagementFormField;